import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { ChefHat, Trash2, Package, Minus, Plus, Database, Calendar, AlertTriangle, TrendingDown, RefreshCw, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { 
  fetchUserMaterials, 
  fetchUserTransactions, 
  fetchUserMenuItems, 
  fetchUserOrders, 
  fetchUserRecipes 
} from '@/utils/userDataFilter';
import { 
  calculateEnhancedWaste,
  type EnhancedWasteCalculationResult
} from '@/utils/physicalWasteCalculationUtils';
import { startOfDay, subDays } from 'date-fns';
import * as XLSX from 'xlsx';

interface MenuItemCost {
  id: string;
  name: string;
  base_price: number;
  cost_per_unit: number;
  profit_margin: number;
  profit_margin_percentage: number;
  ingredients_cost: number;
  labor_cost: number;
  overhead_cost: number;
  total_cost: number;
  sales_count: number;
  revenue: number;
  profit: number;
  efficiency_score: number;
}

interface MaterialWasteData {
  material_id: string;
  material_name: string;
  total_restocked: number;
  total_used: number;
  total_waste: number;
  waste_percentage: number;
  last_restock_date: string;
  restock_cycles_count: number;
  current_stock: number;
  used_in_period: number;
  waste_in_period: number;
  entered_in_period: number;
}

interface AdvancedAnalyticsTablesProps {
  selectedBranchId: string;
}

export default function AdvancedAnalyticsTables({ selectedBranchId }: AdvancedAnalyticsTablesProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [menuItemCosts, setMenuItemCosts] = useState<MenuItemCost[]>([]);
  const [materialWasteData, setMaterialWasteData] = useState<MaterialWasteData[]>([]);
  const [loading, setLoading] = useState(true);
  const [wasteDataLoading, setWasteDataLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [timeRange] = useState('30'); // 30 days default

  useEffect(() => {
    if (user && selectedBranchId) {
      loadMenuItemCosts();
      loadMaterialWasteData();
    }
  }, [user, selectedBranchId]);

  const loadMenuItemCosts = async () => {
    if (!user) return;
    setLoading(true);

    try {
      const [
        { data: menuItemsData },
        { data: recipesData },
        { data: materialsData },
        { data: ordersData },
        { data: expensesData }
      ] = await Promise.all([
        fetchUserMenuItems(user.id, selectedBranchId),
        fetchUserRecipes(user.id, selectedBranchId),
        fetchUserMaterials(user.id, selectedBranchId),
        fetchUserOrders(user.id, parseInt(timeRange), selectedBranchId),
        (supabase as any).from('expenses').select(`
          *,
          category:expense_categories(name),
          branch_expenses!inner(branch_id, allocated_amount)
        `).eq('user_id', user.id).eq('branch_expenses.branch_id', selectedBranchId)
      ]);

      const menuItems = menuItemsData || [];
      const recipes = recipesData || [];
      const materials = materialsData || [];
      const orders = ordersData || [];
      const expenses = expensesData || [];

      const costs = calculateMenuItemCosts(menuItems, recipes, materials, orders, expenses);
      setMenuItemCosts(costs);

    } catch (error) {
      console.error('Error loading menu item costs:', error);
      toast({
        title: 'Error loading menu costs',
        description: 'Failed to load menu item cost data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadMaterialWasteData = async () => {
    if (!user) return;
    
    setWasteDataLoading(true);
    
    try {
      const now = new Date();
      const rangeStart = subDays(startOfDay(now), parseInt(timeRange));
      
      const [periodDeductions, periodRestocks, periodPhysicalCounts, allMaterials] = await Promise.all([
        (supabase as any)
          .from('inventory_transactions')
          .select('material_id, quantity, transaction_type, created_at')
          .eq('user_id', user.id)
          .eq('branch_id', selectedBranchId)
          .eq('transaction_type', 'deduction')
          .gte('created_at', rangeStart.toISOString()),
        (supabase as any)
          .from('inventory_transactions')
          .select('material_id, quantity, transaction_type, created_at')
          .eq('user_id', user.id)
          .eq('branch_id', selectedBranchId)
          .eq('transaction_type', 'restock')
          .gte('created_at', rangeStart.toISOString()),
        (supabase as any)
          .from('physical_count_records')
          .select('material_id, waste_amount, expected_stock, actual_stock, waste_percentage, counted_at')
          .eq('user_id', user.id)
          .eq('branch_id', selectedBranchId)
          .gte('counted_at', rangeStart.toISOString()),
        (supabase as any)
          .from('raw_materials')
          .select('id, name, current_stock')
          .eq('user_id', user.id)
          .eq('branch_id', selectedBranchId)
      ]);

      const usedInPeriodMap = new Map<string, number>();
      for (const d of periodDeductions.data || []) {
        usedInPeriodMap.set(d.material_id, (usedInPeriodMap.get(d.material_id) || 0) + (d.quantity || 0));
      }
      
      const enteredInPeriodMap = new Map<string, number>();
      for (const r of periodRestocks.data || []) {
        enteredInPeriodMap.set(r.material_id, (enteredInPeriodMap.get(r.material_id) || 0) + (r.quantity || 0));
      }

      // Create maps from physical count data
      const wasteInPeriodMap = new Map<string, number>();
      const wastePercentageMap = new Map<string, number>();
      const totalRestockedMap = new Map<string, number>();
      
      for (const w of periodPhysicalCounts.data || []) {
        const materialId = w.material_id;
        wasteInPeriodMap.set(materialId, (wasteInPeriodMap.get(materialId) || 0) + (w.waste_amount || 0));
        
        // Use waste percentage from physical count if available
        if (w.waste_percentage !== null && w.waste_percentage !== undefined) {
          wastePercentageMap.set(materialId, w.waste_percentage);
        }
        
        // Track expected stock as total restocked
        if (w.expected_stock) {
          totalRestockedMap.set(materialId, (totalRestockedMap.get(materialId) || 0) + w.expected_stock);
        }
      }
      
      // Create material map for names and current stock
      const materialMap = new Map<string, any>();
      for (const m of allMaterials.data || []) {
        materialMap.set(m.id, m);
      }
      
      // Build waste data from all materials
      const wasteData: MaterialWasteData[] = [];
      const processedMaterials = new Set<string>();
      
      // First, add all materials from physical counts
      for (const w of periodPhysicalCounts.data || []) {
        const materialId = w.material_id;
        if (processedMaterials.has(materialId)) continue;
        processedMaterials.add(materialId);
        
        const material = materialMap.get(materialId);
        if (!material) continue;
        
        const totalRestocked = totalRestockedMap.get(materialId) || enteredInPeriodMap.get(materialId) || 0;
        const totalUsed = usedInPeriodMap.get(materialId) || 0;
        const totalWaste = wasteInPeriodMap.get(materialId) || 0;
        const wastePercentage = wastePercentageMap.get(materialId) || 
                               (totalRestocked > 0 ? (totalWaste / totalRestocked) * 100 : 0);
        
        wasteData.push({
          material_id: materialId,
          material_name: material.name,
          total_restocked: totalRestocked,
          total_used: totalUsed,
          total_waste: totalWaste,
          waste_percentage: wastePercentage,
          entered_in_period: enteredInPeriodMap.get(materialId) || 0,
          used_in_period: totalUsed,
          waste_in_period: totalWaste,
          last_restock_date: '',
          restock_cycles_count: 0,
          current_stock: material.current_stock || 0
        });
      }
      
      // Then add materials with transactions but no physical counts
      for (const material of allMaterials.data || []) {
        const materialId = material.id;
        if (processedMaterials.has(materialId)) continue;
        
        const totalRestocked = enteredInPeriodMap.get(materialId) || 0;
        const totalUsed = usedInPeriodMap.get(materialId) || 0;
        
        // Only add if there were transactions
        if (totalRestocked > 0 || totalUsed > 0) {
          processedMaterials.add(materialId);
          
          wasteData.push({
            material_id: materialId,
            material_name: material.name,
            total_restocked: totalRestocked,
            total_used: totalUsed,
            total_waste: 0,
            waste_percentage: 0,
            entered_in_period: totalRestocked,
            used_in_period: totalUsed,
            waste_in_period: 0,
            last_restock_date: '',
            restock_cycles_count: 0,
            current_stock: material.current_stock || 0
          });
        }
      }
      
      setMaterialWasteData(wasteData);
      
    } catch (error) {
      console.error('Error loading waste data:', error);
      toast({
        title: 'Error loading waste data',
        description: 'Failed to load material waste data',
        variant: 'destructive'
      });
    } finally {
      setWasteDataLoading(false);
    }
  };

  const calculateMenuItemCosts = (menuItems: any[], recipes: any[], materials: any[], orders: any[], expenses: any[]): MenuItemCost[] => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const isLaborExpense = (expense: any) => {
      const categoryName = expense.category?.name?.toLowerCase() || '';
      const laborKeywords = ['salary', 'salaries', 'wage', 'wages', 'labor', 'staff', 'employee', 'payroll', 'personnel', 'human resources', 'hr'];
      return laborKeywords.some(keyword => categoryName.includes(keyword));
    };
    
    const laborExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.expense_date);
      return expenseDate.getMonth() === currentMonth && 
             expenseDate.getFullYear() === currentYear &&
             isLaborExpense(expense);
    });
    
    const overheadExpenses = expenses.filter(expense => {
      const expenseDate = new Date(expense.expense_date);
      return expenseDate.getMonth() === currentMonth && 
             expenseDate.getFullYear() === currentYear &&
             !isLaborExpense(expense);
    });
    
    const totalMonthlyLaborExpenses = laborExpenses.reduce((sum, expense) => {
      const branchExpense = expense.branch_expenses?.[0];
      return sum + (branchExpense?.allocated_amount || 0);
    }, 0);
    
    const totalMonthlyOverheadExpenses = overheadExpenses.reduce((sum, expense) => {
      const branchExpense = expense.branch_expenses?.[0];
      return sum + (branchExpense?.allocated_amount || 0);
    }, 0);

    const totalSalesQuantity = orders.reduce((total, order) => {
      const orderDate = new Date(order.created_at);
      if (orderDate.getMonth() === currentMonth && 
          orderDate.getFullYear() === currentYear &&
          order.order_items) {
        return total + order.order_items.reduce((sum, item: any) => sum + (item.quantity || 1), 0);
      }
      return total;
    }, 0);

    return menuItems.map(item => {
      const recipe = recipes.find(r => r.menu_item_id === item.id);
      const itemOrders = orders.flatMap(o => o.order_items).filter(oi => oi.menu_item_id === item.id);
      
      let ingredientsCost = 0;
      if (recipe && recipe.recipe_ingredients) {
        ingredientsCost = recipe.recipe_ingredients.reduce((total: number, ingredient: any) => {
          const material = materials.find(m => m.id === ingredient.material_id);
          return total + (material ? ingredient.quantity * material.cost_per_unit : 0);
        }, 0);
      }

      let laborCost = 0;
      const itemSalesQuantity = orders.reduce((total, order) => {
        const orderDate = new Date(order.created_at);
        if (orderDate.getMonth() === currentMonth && 
            orderDate.getFullYear() === currentYear &&
            order.order_items) {
          const itemQuantity = order.order_items
            .filter((orderItem: any) => orderItem.menu_item_id === item.id)
            .reduce((sum, orderItem: any) => sum + (orderItem.quantity || 1), 0);
          return total + itemQuantity;
        }
        return total;
      }, 0);

      if (totalMonthlyLaborExpenses > 0 && totalSalesQuantity > 0 && itemSalesQuantity > 0) {
        laborCost = (totalMonthlyLaborExpenses * itemSalesQuantity) / totalSalesQuantity;
      }

      let overheadCost = 0;
      if (totalMonthlyOverheadExpenses > 0 && totalSalesQuantity > 0 && itemSalesQuantity > 0) {
        overheadCost = (totalMonthlyOverheadExpenses * itemSalesQuantity) / totalSalesQuantity;
      }

      const totalCost = ingredientsCost + laborCost + overheadCost;
      const salesCount = itemOrders.length;
      const revenue = salesCount * item.base_price;
      const profit = revenue - (totalCost * salesCount);
      const profitMargin = item.base_price - totalCost;
      const profitMarginPercentage = item.base_price > 0 ? (profitMargin / item.base_price) * 100 : 0;
      const efficiencyScore = Math.min(100, (profitMarginPercentage * 0.7) + (Math.min(salesCount, 10) * 3));

      return {
        id: item.id,
        name: item.name,
        base_price: item.base_price,
        cost_per_unit: totalCost,
        profit_margin: profitMargin,
        profit_margin_percentage: profitMarginPercentage,
        ingredients_cost: ingredientsCost,
        labor_cost: laborCost,
        overhead_cost: overheadCost,
        total_cost: totalCost,
        sales_count: salesCount,
        revenue,
        profit,
        efficiency_score: efficiencyScore
      };
    });
  };

  const formatCurrency = (amount: number) => {
    return `EGP ${amount.toFixed(2)}`;
  };

  const getEfficiencyColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const downloadExcel = () => {
    if (materialWasteData.length === 0) {
      toast({
        title: "No Data Available",
        description: "There's no material waste data to export.",
        variant: "destructive",
      });
      return;
    }

    setDownloadLoading(true);

    try {
      const excelData = materialWasteData.map((material) => ({
        'Material Name': material.material_name,
        'Total Restocked': material.total_restocked.toFixed(2),
        'Total Used': material.total_used.toFixed(2),
        'Total Waste': material.total_waste.toFixed(2),
        'Waste Percentage': `${material.waste_percentage.toFixed(1)}%`,
        'Current Stock': material.current_stock.toFixed(2),
        'Restock Cycles': material.restock_cycles_count,
        'Last Restock Date': material.last_restock_date ? new Date(material.last_restock_date).toLocaleDateString() : 'No restocks',
        'Used in Period': material.used_in_period.toFixed(2),
        'Waste in Period': material.waste_in_period.toFixed(2),
        'Entered in Period': material.entered_in_period.toFixed(2),
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      const columnWidths = [
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
      ];
      worksheet['!cols'] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Material Waste Analysis');

      const fileName = `Material_Waste_Analysis_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast({
        title: 'Export Successful',
        description: `Waste data exported to ${fileName}`,
        variant: 'default'
      });
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast({
        title: 'Export Failed',
        description: 'Failed to export waste data to Excel',
        variant: 'destructive'
      });
    } finally {
      setDownloadLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Menu Item Cost Analysis Table */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-blue-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ChefHat className="h-6 w-6 text-blue-600" />
                </div>
                Menu Item Cost Analysis
              </CardTitle>
              <CardDescription className="text-gray-600 mt-2">
                Detailed cost breakdown per menu item showing pricing, costs, margins, and efficiency
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80">
                  <TableHead className="font-semibold text-gray-700 bg-gray-100">Item</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Base Price</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Cost</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Profit Margin</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Sales</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Revenue</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Efficiency</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-500">Loading menu cost data...</p>
                    </TableCell>
                  </TableRow>
                ) : menuItemCosts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <ChefHat className="h-12 w-12 text-gray-300" />
                        <p className="font-medium">No menu cost data available</p>
                        <p className="text-sm">Process some orders to see menu item costs and profitability</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  menuItemCosts.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-center">{formatCurrency(item.base_price)}</TableCell>
                      <TableCell className="text-center">{formatCurrency(item.total_cost)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span className={item.profit_margin_percentage > 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(item.profit_margin)}
                          </span>
                          <Badge variant={item.profit_margin_percentage > 30 ? 'default' : 'secondary'}>
                            {item.profit_margin_percentage.toFixed(1)}%
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">{item.sales_count}</TableCell>
                      <TableCell className="text-center">{formatCurrency(item.revenue)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Progress value={item.efficiency_score} className="w-16" />
                          <span className={getEfficiencyColor(item.efficiency_score)}>
                            {item.efficiency_score.toFixed(0)}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Material Waste Analysis Table */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 border-b border-red-100">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-3 text-xl font-bold text-gray-800">
                <div className="p-2 bg-red-100 rounded-lg">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                Material Waste Analysis
              </CardTitle>
              <CardDescription className="text-gray-600 mt-2">
                Detailed waste analysis per material showing restock cycles, usage, and waste percentages
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                {materialWasteData.length} Materials
              </Badge>
              {wasteDataLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Loading...
                </div>
              )}
              <Button onClick={downloadExcel} variant="outline" size="sm" className="flex items-center gap-2" disabled={downloadLoading}>
                {downloadLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {downloadLoading ? 'Exporting...' : 'Export to Excel'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="font-semibold text-gray-700 bg-gray-100">Material</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Total Restocked</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Total Used</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Total Waste</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Waste %</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Current Stock</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Restock Cycles</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Last Restock</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Used in Period</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Waste in Period</TableHead>
                  <TableHead className="font-semibold text-gray-700 bg-gray-100 text-center">Entered in Period</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wasteDataLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12">
                      <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-gray-400" />
                      <p className="text-gray-500">Loading waste data...</p>
                    </TableCell>
                  </TableRow>
                ) : materialWasteData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center py-12 text-gray-500">
                      <div className="flex flex-col items-center gap-2">
                        <Trash2 className="h-12 w-12 text-gray-300" />
                        <p className="font-medium">No waste data available</p>
                        <p className="text-sm">Record transactions and inventory counts to track material waste</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  materialWasteData.map((material, index) => (
                    <TableRow 
                      key={material.material_id} 
                      className={`hover:bg-gray-50/80 transition-colors ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                      }`}
                    >
                      <TableCell className="font-semibold text-gray-800">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${
                            material.waste_percentage > 20 ? 'bg-red-500' : 
                            material.waste_percentage > 10 ? 'bg-orange-500' : 'bg-green-500'
                          }`} />
                          {material.material_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Package className="h-4 w-4 text-blue-500" />
                          <span className="font-medium text-gray-700">
                            {material.total_restocked.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Minus className="h-4 w-4 text-green-500" />
                          <span className="font-medium text-gray-700">
                            {material.total_used.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Trash2 className="h-4 w-4 text-red-500" />
                          <span className={`font-semibold ${
                            material.total_waste > 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {material.total_waste.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-2">
                          <div className="relative w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all duration-300 ${
                                material.waste_percentage > 20 ? 'bg-red-500' : 
                                material.waste_percentage > 10 ? 'bg-orange-500' : 'bg-green-500'
                              }`}
                              style={{ width: `${Math.min(material.waste_percentage, 100)}%` }}
                            />
                          </div>
                          <span className={`font-semibold text-sm ${
                            material.waste_percentage > 20 ? 'text-red-600' : 
                            material.waste_percentage > 10 ? 'text-orange-600' : 'text-green-600'
                          }`}>
                            {material.waste_percentage.toFixed(1)}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Database className="h-4 w-4 text-blue-500" />
                          <span className={`font-semibold ${
                            material.current_stock > 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {material.current_stock.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {material.restock_cycles_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {material.last_restock_date ? (
                          <div className="flex items-center justify-center gap-1">
                            <Calendar className="h-4 w-4 text-gray-500" />
                            <span className="text-sm text-gray-600">
                              {new Date(material.last_restock_date).toLocaleDateString()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">No restocks</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <TrendingDown className="h-4 w-4 text-green-500" />
                          <span className="font-medium text-gray-700">
                            {material.used_in_period.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          <span className={`font-semibold ${
                            material.waste_in_period > 0 ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {material.waste_in_period.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Plus className="h-4 w-4 text-blue-500" />
                          <span className="font-medium text-gray-700">
                            {material.entered_in_period.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
